import type { PropsWithChildren } from 'react';
import { cn } from '@/lib/cn';
import Icon from '../Icon/Icon';
import { Button } from '../ui/button';
import { HoverCard, HoverCardContent, HoverCardTrigger } from './HoverCard';
type Props = PropsWithChildren<{
  /** ツールチップの表示位置 */
  side?: 'left' | 'right' | 'bottom' | 'top';
  className?: string;
}>;

/**
 * ヘルプ情報を表示するホバーカードコンポーネント
 *
 * @remarks
 * このコンポーネントは、ユーザーがヘルプアイコンにホバーした時にのみ情報を表示します。
 *
 * @example
 * ```tsx
 * <HelpHoverCard side="right">
 *   <p>ヘルプ内容をここに記述</p>
 * </HelpHoverCard>
 * ```
 *
 * @see https://github.com/shadcn-ui/ui/issues/3989
 * Drawerやポップオーバーが開いたときに、その中のHoverCardのトリガー要素が自動的にフォーカスを受け取ってしまい、HoverCardContentが意図せず表示されるissueがopenのため、
 * この問題を回避するため、トリガーボタンにtabIndex={-1}を設定してフォーカスを無効化しています。
 */
export default function HelpHoverCard({
  side,
  className,
  children
}: Props) {
  return <HoverCard>
      <HoverCardTrigger asChild>
        <Button size="icon" variant="ghost" className={cn('size-5', className)} onClick={e => e.stopPropagation()} tabIndex={-1}>
          <Icon iconName="HelpCircleIcon" className="stroke-muted-foreground" />
        </Button>
      </HoverCardTrigger>
      <HoverCardContent side={side} align="start" sideOffset={10} className="z-50 w-[260px] rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-hidden data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95">
        {children}
      </HoverCardContent>
    </HoverCard>;
}